import { useState } from 'react';
import type { FormEvent } from 'react';
import QRCode from 'qrcode';
import { supabase } from './supabase.js';
import { CHAT_API_BASE } from './config.js';

// Door-label printer (Étiquette tab): one 63.5 x 38.1mm peel-and-stick label
// per room (Avery L7160 — 3 columns x 7 rows, 21/sheet), each carrying the
// room number, a QR to the guest chat (with a one-line French « what is this »
// hint under it — guests rarely scanned it unprompted), and the shared
// front-door entry code.
// The QR token is signed server-side (/api/chat/labels) — CHAT_QR_SECRET
// never reaches the browser; this component only turns the returned tokens
// into QR images and lays out the printable sheet.

const LABELS_PER_SHEET = 21; // 3 cols x 7 rows, 63.5 x 38.1mm

function range(from: number, to: number): string[] {
  const out: string[] = [];
  for (let n = from; n <= to; n++) out.push(String(n));
  return out;
}

// The hotel's 42-room scheme (4 floors; 13/13/13/3 — 113/213/313 skipped).
const ROOMS: string[] = [
  ...range(101, 112), '114',
  ...range(201, 212), '214',
  ...range(301, 312), '314',
  ...range(401, 403),
];
const MAX_SUBSET_ROOMS = 21; // one full sheet

type RoomMode = 'all' | 'subset';

/** "101-106 201 205" → ['101',...,'106','201','205'] — space-separated, "-" for ranges. */
function parseRoomSpec(spec: string): string[] {
  const rooms: string[] = [];
  for (const token of spec.trim().split(/\s+/).filter(Boolean)) {
    const range = token.match(/^(\d+)-(\d+)$/);
    if (range) {
      const [, from, to] = range;
      for (let n = Number(from); n <= Number(to); n++) rooms.push(String(n));
    } else {
      rooms.push(token);
    }
  }
  return rooms;
}

interface Label {
  room: string;
  qrDataUrl: string;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export function Labels() {
  const [doorCode, setDoorCode] = useState(() => {
    // No built-in default: the code is a secret and rotates. It is remembered
    // on this device after the first successful generation, never shipped.
    const stored = localStorage.getItem('reception.doorCode');
    return stored && stored.trim() !== '' ? stored : '';
  });
  const [mode, setMode] = useState<RoomMode>('all');
  const [subsetSpec, setSubsetSpec] = useState('');
  const [labels, setLabels] = useState<Label[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const codeMissing = doorCode.trim() === '';

  async function generate(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setError('');

    if (codeMissing) {
      setError('Le code de la porte d’entrée est manquant — saisissez-le avant de générer.');
      return;
    }
    const code = doorCode.trim();

    let rooms: string[];
    if (mode === 'all') {
      rooms = ROOMS;
    } else {
      rooms = parseRoomSpec(subsetSpec);
      if (rooms.length === 0) {
        setError('Indiquez au moins une chambre (ex. « 101-106 » ou « 101 102 103 »).');
        return;
      }
      if (rooms.length > MAX_SUBSET_ROOMS) {
        setError(`Trop de chambres pour une feuille (${rooms.length}, maximum ${MAX_SUBSET_ROOMS}).`);
        return;
      }
    }

    setBusy(true);
    setLabels([]);
    try {
      localStorage.setItem('reception.doorCode', code);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('session expirée — reconnectez-vous');

      const res = await fetch(`${CHAT_API_BASE}/api/chat/labels`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ rooms }),
      });
      if (!res.ok) throw new Error(`génération impossible (${res.status})`);
      const data = (await res.json()) as { tokens: { room: string; token: string }[] };

      const built = await Promise.all(
        data.tokens.map(async ({ room, token }) => {
          const url = `${CHAT_API_BASE}/chat?room=${encodeURIComponent(room)}&t=${token}`;
          const qrDataUrl = await QRCode.toDataURL(url, { margin: 0, errorCorrectionLevel: 'M' });
          return { room, qrDataUrl };
        }),
      );
      setLabels(built);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'génération impossible');
    } finally {
      setBusy(false);
    }
  }

  const sheets = chunk(labels, LABELS_PER_SHEET);

  return (
    <div className="labels-page">
      <h2>Étiquettes de porte</h2>
      <form className="labels-form" onSubmit={(e) => void generate(e)}>
        <label className="labels-field">
          <span>Code de la porte d’entrée</span>
          <input
            type="text"
            value={doorCode}
            onChange={(e) => setDoorCode(e.target.value)}
            placeholder="code de la porte"
            maxLength={20}
          />
          {codeMissing && (
            <span className="labels-warning" role="alert">
              ⚠️ Code de la porte non renseigné — il sera imprimé sur chaque étiquette.
            </span>
          )}
        </label>

        <fieldset className="labels-mode">
          <legend>Chambres</legend>
          <label className="labels-radio">
            <input
              type="radio"
              name="room-mode"
              checked={mode === 'all'}
              onChange={() => setMode('all')}
            />
            Toutes les chambres ({ROOMS.length})
          </label>
          <label className="labels-radio">
            <input
              type="radio"
              name="room-mode"
              checked={mode === 'subset'}
              onChange={() => setMode('subset')}
            />
            Sélection (jusqu’à {MAX_SUBSET_ROOMS})
          </label>
          {mode === 'subset' && (
            <input
              type="text"
              className="labels-subset-input"
              value={subsetSpec}
              onChange={(e) => setSubsetSpec(e.target.value)}
              placeholder="ex. 101-106 201 205"
            />
          )}
        </fieldset>

        <button type="submit" disabled={busy || codeMissing}>
          {busy ? 'Génération…' : 'Générer'}
        </button>
        {labels.length > 0 && (
          <button type="button" onClick={() => window.print()}>
            Imprimer
          </button>
        )}
      </form>

      {error && <p className="labels-error">{error}</p>}

      {sheets.length > 0 && (
        <p className="labels-hint">
          {labels.length} chambre(s) → {sheets.length} feuille(s) de {LABELS_PER_SHEET} étiquettes
          (63,5 x 38,1mm). Imprimez à 100 % (pas d’ajustement à la page).
        </p>
      )}

      <div className="print-area">
        {sheets.map((sheetLabels, i) => (
          <div className="sheet" key={i}>
            {sheetLabels.map(({ room, qrDataUrl }) => (
              <div className="label" key={room}>
                <div className="label-inner">
                  <div className="room">{room}</div>
                  <div className="qr">
                    <img src={qrDataUrl} alt={`QR chambre ${room}`} />
                  </div>
                  <div className="hint">Information &amp; Réception</div>
                  <div className="code">{doorCode.trim()}</div>
                </div>
              </div>
            ))}
            {sheetLabels.length < LABELS_PER_SHEET &&
              Array.from({ length: LABELS_PER_SHEET - sheetLabels.length }).map((_, i) => (
                <div className="label label-empty" key={`pad-${i}`} />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
